import styled from '@emotion/styled';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { ContentBodyContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { Color } from '@diritto/common/style/color';
import { useGetActivitySummaryQuery } from 'queries/activityPoint/useGetActivitySummaryQuery';
import {
  ActivityPointType,
  ActivityPointTypeChanceString,
  ActivityPointTypeDisplayList,
  ActivityPointTypeString,
} from '@diritto/common/activityPoint/constants';
import React, { useMemo } from 'react';
import { Loading } from 'ui-components/common/Loading';
import { css } from '@emotion/react';
import { ArrowHorizontalIcon } from 'icons/ArrowHorizontalIcon';
import { Link } from 'ui-components/common/Link';
import { DateTime } from 'luxon';
import { LuxonToWeekDay, WeekDayString } from '@diritto/common/utils/date';

interface QuestProps {
  questType: ActivityPointType;
  questName: string;
  questPoint: number;
  questDoneCount: number;
  questTotalCount: number;
  questChance: string;
}

const Quest = ({ questType, questName, questPoint, questDoneCount, questTotalCount, questChance }: QuestProps) => {
  const progressPercent = useMemo(() => (100 / questTotalCount) * questDoneCount, [questDoneCount, questTotalCount]);
  return (
    <StyledQuest>
      <StyledQuestInfo>
        <StyledLeft>
          {questType === ActivityPointType.AttendanceCheck ? (
            <Link href="/quest/attendance-check">
              <StyledLinkedQuestName>
                {questName}
                <ArrowHorizontalIcon direction="right" size={16} />
              </StyledLinkedQuestName>
            </Link>
          ) : (
            <StyledQuestName>{questName}</StyledQuestName>
          )}
          <StyledQuestPoint>{questChance ? `${questPoint}점 / ${questChance}` : `${questPoint}점`}</StyledQuestPoint>
        </StyledLeft>
        <StyledQuestCount>
          {questDoneCount}번 / 총 {questTotalCount}번
        </StyledQuestCount>
      </StyledQuestInfo>
      <StyledBarWrapper>
        <StyledBackgroundBar />
        <StyledProgressBar progress={progressPercent} />
      </StyledBarWrapper>
    </StyledQuest>
  );
};
export const QuestList = () => {
  const today = DateTime.now().setZone('Asia/Seoul').set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  const weekDayString = WeekDayString[LuxonToWeekDay[today.weekday]];
  const DateString = `${today.year}년 ${today.month}월 ${today.day}일 ${weekDayString}요일`;
  const yearMonthDay = today.toISODate();

  const { data: summaryData, isPending: isSummaryDataPending } = useGetActivitySummaryQuery(yearMonthDay);

  const isLoading = isSummaryDataPending;
  const summary = summaryData?.summary;
  const pointPolicy = summaryData?.season.season.extraInfo.payload.pointPolicy;

  if (isLoading) {
    return <StyledLoading />;
  }

  if (!pointPolicy || !summary) {
    return null;
  }

  return (
    <ContentBodyContainer>
      <StyledQuestListWrapper>
        <StyledQuestTitle>{DateString}</StyledQuestTitle>
        <StyledQuestWrapper>
          {ActivityPointTypeDisplayList.map(activityPointType => {
            const activityPointPolicy = pointPolicy[activityPointType];
            const activitySummary = summary[activityPointType];

            if (activityPointPolicy === undefined || activitySummary === undefined) {
              return null;
            }

            if (activityPointPolicy.limitPerDay === 0) {
              return null;
            }

            return (
              <Quest
                key={activityPointType}
                questType={activityPointType}
                questName={ActivityPointTypeString[activityPointType]}
                questPoint={activityPointPolicy.point}
                questDoneCount={activitySummary}
                questTotalCount={activityPointPolicy.limitPerDay}
                questChance={ActivityPointTypeChanceString[activityPointType]}
              />
            );
          })}
        </StyledQuestWrapper>
      </StyledQuestListWrapper>
    </ContentBodyContainer>
  );
};

const StyledLoading = styled(Loading)`
  display: flex;
  height: 50vh;
`;

const StyledQuestListWrapper = styled.div`
  padding-top: 16px;
  padding-bottom: 24px;
`;

const StyledQuestWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 8px;
  gap: 24px;
`;

const StyledQuestTitle = styled.div`
  ${Typography.Small};
  font-weight: ${FontWeight.Bold};
  margin-top: 8px;
`;

const StyledQuest = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const StyledQuestInfo = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const StyledLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledQuestName = styled.div`
  ${Typography.XXSmall};
  font-weight: ${FontWeight.Medium};
`;

const StyledLinkedQuestName = styled(StyledQuestName)`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StyledQuestPoint = styled.div`
  font-weight: ${FontWeight.Medium};
  ${Typography.XXXSmall};
  color: ${Color.Gray500};
`;

const StyledQuestCount = styled.div`
  font-weight: ${FontWeight.Medium};
  ${Typography.XXXSmall};
  color: ${Color.Gray600};
`;

const StyledBarWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
  margin-top: 8px;
  border-radius: 12px;
  overflow: hidden;
`;

const StyledBackgroundBar = styled.div`
  width: 100%;
  height: 10px;
  position: absolute;
  background-color: ${Color.Gray200};
`;

const StyledProgressBar = styled.div(
  ({ progress }: { progress: number }) => css`
    width: ${progress}%;
    height: 10px;
    position: absolute;
    background: linear-gradient(90deg, ${Color.Blue400}, ${Color.Primary500});
  `,
);
